import { formatUnitMantissa, pickUnitDisplay } from '#core/components/dashboard/number-format.ts';
import { type ColumnType, unitOfType } from '#core/lib/expr/schema/types.ts';
import { formatUnit, isLaddered } from '#core/lib/units/registry.ts';

/**
 * Display string for a column's declared unit, or '' when the column is
 * unit-less or its unit cancels to dimensionless. SSOT bridge from an
 * engine {@link ColumnType} to the rendered unit shown beside values.
 *
 * This is the magnitude-independent label (e.g. `STB/d`, `psi`) — for a
 * laddered dimension it names the canonical base rung. Render sites that
 * scale onto a magnitude rung (KPI / chart axis / table column) use
 * {@link ladderedUnitDisplay} instead, which folds the magnitude into the
 * petroleum unit prefix.
 */
export const unitLabel = (type: ColumnType | undefined): string => {
  const unit = type === undefined ? undefined : unitOfType(type);
  return unit ? formatUnit(unit) : '';
};

/**
 * Unit-aware display for a numeric column whose declared dimension carries a
 * magnitude ladder (liquid/gas volume + rate). Returns `undefined` for unit-less
 * columns and for dimensions with a single fixed display symbol (pressure,
 * temperature, money, …) — those keep their existing label + numeric formatter.
 *
 * The chosen rung is FIXED across the whole column from its group max, so every
 * value in a chart axis / table column / related-KPI group renders on the same
 * rung and stays visually comparable. The returned `unit` is the laddered label
 * (e.g. `MMSCF/d`) for the header/title; `format` scales a single canonical-base
 * value onto that rung WITHOUT re-appending the unit (the unit rides on the
 * header/title, not per-cell). For a standalone KPI pass the latest value alone
 * as `values` to get the auto rung.
 */
export interface LadderedUnitDisplay {
  /** Laddered unit label of the fixed rung (e.g. `MMSCF/d`) — shown in the header/title. */
  readonly unit: string;
  /** Scale a canonical-base value onto the fixed rung; returns the bare mantissa string. */
  readonly format: (vBase: number) => string;
}

export const ladderedUnitDisplay = (
  type: ColumnType | undefined,
  values: readonly number[],
): LadderedUnitDisplay | undefined => {
  const unit = type === undefined ? undefined : unitOfType(type);
  if (unit === undefined || !isLaddered(unit)) {
    return undefined;
  }
  // Fix the rung from the largest-magnitude value so the whole group shares one
  // rung; `vBase === 0` clamps to the base rung, so an all-zero column is safe.
  const groupMax = values.reduce((max, v) => (Math.abs(v) > Math.abs(max) ? v : max), 0);
  const chosen = pickUnitDisplay(groupMax, unit);
  const rung = chosen.def.slug;
  return {
    unit: chosen.unit,
    format: (vBase: number) => formatUnitMantissa(vBase, unit, rung),
  };
};
